import {
  Box,
  Button,
  HStack,
  Text,
  Checkbox,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  useToast,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import {
  ArrowCounterClockwise,
  Check,
  DotsThreeVertical,
  DownloadSimple,
  Eye,
  MinusCircle,
  Plus,
  Trash,
} from "@phosphor-icons/react";
import LocalSpinner from "components/AppSpinner/AppSpinner";
import DataTableV2 from "components/DataTable/DateTableV2";
import React, { useEffect, useState } from "react";
import { colors } from "theme/foundations/colors";
import { formatDate, showErrorToast } from "utils";
import { useRightSidebar } from "context/RightSidebarContext";
import SourceDetailsPanel from "./SourceDetailsRSidebar";
import { getFileType, getFolderForRound, KB_FOLDER } from "api/common/fileinfo";
import { useQuery, useQueryClient } from "react-query";
import { queryKey } from "api/api-utils";
import fileinfoService, {
  apiListUsage,
  apiArchiveFileInfo,
  apiRestoreFromArchiveFileInfo,
  apiAddToFolders,
  apiRemoveFromFolders,
} from "api/common/fileinfo.service";
import Common from "api/common/common";
import { H2 } from "components/text/headings";
import SourcesUploadRSidebar from "./SourcesUploadRSidebar";
import { useSelector } from "react-redux";
import useAirUserRoles from "utils/AuthUtils";
import { useNavigate } from "react-router-dom";
import { routes } from "config/paths";
import { gotoFilteredQuestionList } from "../Dashboard/components/AIDashboard/AIDashboard";
import { MultiSelect } from "../../../components";
import questionnaireService from "../../../api/issb/questionnaire.service";
import { issbKeys } from "../../../hooks/query";
import { useAuth } from "../../../context";
import { Card } from "../../../components/Card";
import SourcesRoundsAsSourceSelector from "./SourcesRoundsAsSourceSelector";

const SELECTED_COLUMN_ID = "selected";

/** Wraps DataTableV2 with an independent search filter on filename in the Title column header */
function SearchableDataTable({ data, columns, searchThreshold = 5, ...tableProps }) {
  const [filter, setFilter] = useState("");
  const showSearch = data?.length > searchThreshold || filter;
  const filtered = filter ? data?.filter((f) => f.filename?.toLowerCase().includes(filter.toLowerCase())) : data;

  // Inject search input into the Title column header
  const searchColumns = columns.map((col) =>
    col.accessorKey === "filename"
      ? {
          ...col,
          header: (
            <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
              <Text>Title</Text>
              {showSearch && (
                <Input
                  borderRadius="full"
                  borderColor="gray.300"
                  color="gray.700"
                  placeholder="Search documents..."
                  type="search"
                  size="sm"
                  width="200px"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              )}
            </Box>
          ),
        }
      : col
  );

  return <DataTableV2 columns={searchColumns} data={filtered || []} emptyMessage="No files found" {...tableProps} />;
}

function SectionSelectToggle({ sectionFileIds, selectedIds, setSelectedIds }) {
  const allSelected = sectionFileIds.every((id) => selectedIds.includes(id));

  const handleToggle = () => {
    if (allSelected) {
      setSelectedIds(selectedIds.filter((id) => !sectionFileIds.includes(id)));
    } else {
      const idsToAdd = sectionFileIds.filter((id) => !selectedIds.includes(id));
      setSelectedIds([...selectedIds, ...idsToAdd]);
    }
  };

  return (
    <Checkbox
      colorScheme="limeShade"
      isChecked={allSelected}
      isIndeterminate={!allSelected && sectionFileIds.some((id) => selectedIds.includes(id))}
      onChange={handleToggle}
    >
      <Text fontSize="sm">{allSelected ? "Deselect All" : "Select All"}</Text>
    </Checkbox>
  );
}

/**
 * File upload and management panel
 * Design: https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=1099-20369&t=ANW7dTdV9oBTWOQ3-0
 *
 * @param {React.ReactNode} HackBelow - A component to render below the main content. Provided
 * as a quick-hack to render the onboarding nav buttons
 * @param {boolean} showFiles - Whether to show the files list. Another quick-hack for the onboarding screens
 *
 * @param {string[]} hideColumns - An array of column ids to hide
 * @param {string[]} selectedIds - An array of file ids to select. If unset, dont show checkboxes.
 * This could also act to mark  the AI Review path vs the Sources setup path.
 * @param {string[]} selectedRounds - An array of round ids to select. If unset, use current-round.sourceRoundIds
 * @param {function} setSelectedRounds - A function to set the selected rounds. If unset, modify current-round.sourceRoundIds and save edits
 * @param {string[]} tables - An array of table names to show: ACTIVE, OTHER, ARCHIVED,KB. If unset, show all tables
 */
export default function SourcesListPanel({
  HackBelow,
  showFiles,
  hideColumns,
  hideAIReviewButton,
  selectedIds,
  setSelectedIds,
  sourceRoundIds,
  setSourceRoundIds,
  allowedRoundIds,
  tables,
  hideRoundsSelector = false,
  hideActiveLabel = false,
}) {
  const { isController } = useAirUserRoles();

  if (!tables) {
    tables = ["ACTIVE"];
    if (isController()) {
      tables.push("OTHER");
      tables.push("ARCHIVED");
    }
  }

  const { setRightSidebar, doOpen } = useRightSidebar();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { getAccessTokenSilently } = useAuth();
  // Get the list of files
  const { data: filesPage, isLoading } = useQuery(["fileinfoService.list", "all"], async () => {
    await getAccessTokenSilently();
    return fileinfoService.list();
  });
  let files = filesPage?.content;
  // Get the list of archived files
  const { data: filesArchivedPage, isLoading: isLoadingArchived } = useQuery(
    ["fileinfoService.list", "archived"],
    async () => {
      await getAccessTokenSilently();
      return fileinfoService.list({ query: "archived:true" });
    }
  );
  // KB files?
  const { data: filesKBPage, isLoading: isLoadingKB } = useQuery(
    ["fileinfoService.list", KB_FOLDER],
    async () => {
      await getAccessTokenSilently();
      return fileinfoService.list({ query: "folder:" + KB_FOLDER });
    },
    {
      enabled: tables?.includes(KB_FOLDER),
    }
  );

  // TODO support deep linking to a file details
  // const [searchParams, setSearchParams] = useSearchParams();
  const roundSelector = useSelector((state) => state.issbRound);
  const roundId = roundSelector?.selectedRound?.id;
  const folder = tables.includes(KB_FOLDER) && tables.length === 1 ? KB_FOLDER : getFolderForRound(roundId);

  const readOnly = hideColumns?.includes("actions");
  const openSidebar = (item) => {
    setRightSidebar(<SourceDetailsPanel item={item} folder={folder} readOnly={readOnly} />);
    doOpen();
  };

  // Get the usage of each file
  const { data: fileUsages } = useQuery(queryKey("apiListUsage"), () => apiListUsage(files?.map((f) => f.id)), {
    enabled: !!files?.length, // don't fetch until we have a list of files
  });
  const usageByFileinfoId = {};
  if (fileUsages) {
    for (const usage of fileUsages) {
      if (!usageByFileinfoId[usage.fileinfoId]) {
        usageByFileinfoId[usage.fileinfoId] = [];
      }
      if (!usageByFileinfoId[usage.fileinfoId].includes(usage.usageId)) {
        usageByFileinfoId[usage.fileinfoId].push(usage.usageId);
      }
    }
  }
  const gotoUsage = (fileinfo) => {
    console.log("gotoUsage", fileinfo);
    const qIds = usageByFileinfoId[fileinfo.id];
    gotoFilteredQuestionList(navigate, qIds, "File " + fileinfo.filename + " usage");
  };

  // Split by in/out of this q round
  let filesInRound = files?.filter((f) => f.folders?.includes(folder));
  let otherFiles = files?.filter((f) => !f.folders?.includes(folder));

  /** @type {ColumnDef[]} */
  let columns = [
    {
      header: "Title",
      accessorKey: "filename",
      cell: ({ row }) => (
        <Text onClick={() => openSidebar(row.original)} cursor="pointer" _hover={{ color: "limeShade.600" }}>
          {row.original.filename}
        </Text>
      ),
      size: 250,
    },
    {
      header: "Type",
      size: 75,
      accessorFn: (file) => getFileType(file),
      cell: ({ cell }) => <Text>{cell.getValue()}</Text>,
    },
    {
      id: "uploadDate",
      header: "Upload date",
      size: 100,
      accessorFn: (file) => formatDate(file.createdDate),
      cell: ({ cell }) => <Text>{cell.getValue()}</Text>,
    },
    {
      id: "usage",
      header: "Usage",
      size: 75,
      accessorFn: (file) => usageByFileinfoId[file.id]?.length || "",
      cell: ({ row, cell }) => (
        <Text cursor="pointer" onClick={() => gotoUsage(row.original)}>
          {cell.getValue()}
        </Text>
      ),
    },
    {
      id: "aiDate",
      header: "AI",
      size: 50,
      accessorFn: (file) => formatDate(file.aiDate),
      cell: ({ cell }) => <span title={cell.getValue()}>{cell.getValue() ? <Check /> : ""}</span>,
    },
    {
      id: "actions",
      header: "",
      size: 50,
      cell: ({ row }) => {
        const file = row.original;
        const isActive = file.folders?.includes(folder);
        const isArchived = file.archived;
        const isLibrary = file.folders?.includes(KB_FOLDER) && !isActive;
        const handleDownload = () => {
          Common.getAttachment({ attachmentId: file.documentId })
            .then(() =>
              toast({
                title: "File downloaded",
                description: file.filename + " has been downloaded.",
                status: "success",
              })
            )
            .catch((err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message));
        };
        const handleArchive = () => {
          apiArchiveFileInfo(file).then(() => {
            queryClient.invalidateQueries("fileinfoService.list");
            queryClient.invalidateQueries(["fileinfoService.list", "archived"]);
          });
        };
        const handleRestore = () => {
          apiRestoreFromArchiveFileInfo(file).then(() => {
            queryClient.invalidateQueries("fileinfoService.list");
            queryClient.invalidateQueries(["fileinfoService.list", "archived"]);
          });
        };
        const handleRemoveFromRound = () => {
          apiRemoveFromFolders(file, folder).then(() => {
            queryClient.invalidateQueries("fileinfoService.list");
          });
        };
        const handleAddToActive = () => {
          apiAddToFolders(file, folder).then(() => {
            queryClient.invalidateQueries("fileinfoService.list");
          });
        };
        return (
          <Menu isLazy>
            <MenuButton
              as={IconButton}
              aria-label="Actions"
              icon={<DotsThreeVertical size={20} />}
              variant="ghost"
              size="sm"
              borderRadius="full"
            />
            <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1} fontSize="sm">
              {!isArchived && !isLibrary && (
                <MenuItem icon={<Eye size={16} />} onClick={() => openSidebar(file)}>
                  View Details
                </MenuItem>
              )}
              <MenuItem icon={<DownloadSimple size={16} />} onClick={handleDownload}>
                Download
              </MenuItem>
              {isActive && (
                <MenuItem icon={<MinusCircle size={16} />} onClick={handleRemoveFromRound}>
                  Remove from Round
                </MenuItem>
              )}
              {isArchived && (
                <MenuItem icon={<ArrowCounterClockwise size={16} />} onClick={handleRestore}>
                  Restore to Active
                </MenuItem>
              )}
              {isLibrary && (
                <MenuItem icon={<Plus size={16} />} onClick={handleAddToActive}>
                  Add to Active
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        );
      },
    },
  ];
  if (selectedIds) {
    const sectionFileIds = (filesInRound || []).map((f) => f.id);
    const allSelected = sectionFileIds.length > 0 && sectionFileIds.every((id) => selectedIds.includes(id));
    const someSelected = sectionFileIds.some((id) => selectedIds.includes(id));
    columns.push({
      id: SELECTED_COLUMN_ID,
      header: () => {
        const noneSelected = !sectionFileIds.some((id) => selectedIds.includes(id));
        return (
          <Box textAlign="center">
            <Button
              variant="link"
              size="xs"
              color="gray.500"
              fontWeight="normal"
              onClick={() => {
                if (noneSelected) {
                  setSelectedIds([...selectedIds, ...sectionFileIds]);
                } else {
                  setSelectedIds(selectedIds.filter((id) => !sectionFileIds.includes(id)));
                }
              }}
            >
              {noneSelected ? "Select all" : "Deselect all"}
            </Button>
          </Box>
        );
      },
      size: 50,
      cell: ({ row }) => {
        return (
          <Box textAlign="center">
            <Checkbox
              colorScheme="limeShade"
              isChecked={selectedIds.includes(row.original.id)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedIds([...selectedIds, row.original.id]);
                } else {
                  setSelectedIds(selectedIds.filter((id) => id !== row.original.id));
                }
              }}
            />
          </Box>
        );
      },
    });
  }
  if (hideColumns) {
    columns = columns.filter((c) => !hideColumns.includes(c.id));
  }
  //   console.log("filesInRound", filesInRound, "from", files);

  const gotoUpload = () => {
    setRightSidebar(
      <SourcesUploadRSidebar activeFiles={filesInRound} hideAIReviewButton={hideAIReviewButton} tables={tables} />
    );
    doOpen();
  };

  const navigate = useNavigate();

  return (
    <Box>
      {tables.includes("ACTIVE") && (
        <>
          {
            isLoading && (
              <LocalSpinner />
            ) /*TODO why doesn't this spinner show? Does it need explicit sizing or something? */
          }
          <HStack justify={hideActiveLabel ? "flex-end" : "space-between"} mb={4}>
            {!hideActiveLabel && (
              <H2>
                {roundId ? "Active Documents" : "All Files"}{" "}
                <Text as="span" fontWeight="normal" color="gray.500">
                  ({filesInRound?.length})
                </Text>
              </H2>
            )}
            <Button variant="primary" onClick={gotoUpload} borderRadius="full">
              + Add
            </Button>
          </HStack>
          {filesInRound && (
            <SearchableDataTable
              columns={columns}
              data={filesInRound}
              isLoading={isLoading}
              serverSidePagination={false}
              showPaginator={false}
              maxHeight="500px"
            />
          )}
        </>
      )}

      {roundId && tables.includes("OTHER") && (
        <Accordion allowToggle mt={8}>
          <AccordionItem borderBottom="none">
            <AccordionButton px={0} py={4} _hover={{ bg: "transparent" }}>
              <H2 mt={0} mb={0}>
                Library Documents{" "}
                <Text as="span" fontWeight="normal" color="gray.500">
                  ({otherFiles?.length})
                </Text>
              </H2>
              <AccordionIcon mr={0} ml="auto" boxSize={6} />
            </AccordionButton>
            <AccordionPanel px={0}>
              {otherFiles && (
                <SearchableDataTable
                  columns={columns.filter((c) => c.id !== SELECTED_COLUMN_ID)}
                  data={otherFiles}
                  isLoading={isLoading}
                  serverSidePagination={false}
                  showPaginator={false}
                  maxHeight="400px"
                />
              )}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}

      {tables.includes(KB_FOLDER) && (
        <Accordion allowToggle mt={8}>
          <AccordionItem borderBottom="none">
            <AccordionButton px={0} py={4} _hover={{ bg: "transparent" }}>
              <H2 mt={0} mb={0}>
                Library Documents{" "}
                <Text as="span" fontWeight="normal" color="gray.500">
                  ({filesKBPage?.content?.length})
                </Text>
              </H2>
              <AccordionIcon mr={0} ml="auto" boxSize={6} />
            </AccordionButton>
            <AccordionPanel px={0}>
              {selectedIds && filesKBPage?.content?.length > 0 && (
                <HStack justify="flex-end" mb={4}>
                  <SectionSelectToggle
                    sectionFileIds={filesKBPage.content.map((f) => f.id)}
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                  />
                </HStack>
              )}
              {isLoadingKB && <LocalSpinner />}
              {filesKBPage?.content && (
                <SearchableDataTable
                  columns={columns}
                  data={filesKBPage?.content}
                  isLoading={isLoadingKB}
                  serverSidePagination={false}
                  showPaginator={false}
                  maxHeight="400px"
                />
              )}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}

      {tables.includes("ARCHIVED") && (
        <Accordion allowToggle mt={8}>
          <AccordionItem borderBottom="none">
            <AccordionButton px={0} py={4} _hover={{ bg: "transparent" }}>
              <H2 mt={0} mb={0}>
                Archived Documents{" "}
                <Text as="span" fontWeight="normal" color="gray.500">
                  ({filesArchivedPage?.content?.length})
                </Text>
              </H2>
              <AccordionIcon mr={0} ml="auto" boxSize={6} />
            </AccordionButton>
            <AccordionPanel px={0}>
              {isLoadingArchived && <LocalSpinner />}
              {filesArchivedPage?.content && (
                <SearchableDataTable
                  columns={columns.filter((c) => c.id !== SELECTED_COLUMN_ID)}
                  data={filesArchivedPage?.content}
                  isLoading={isLoadingArchived}
                  serverSidePagination={false}
                  showPaginator={false}
                  maxHeight="400px"
                />
              )}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}

      {!hideRoundsSelector && roundId && isController() && (
        <SourcesRoundsAsSourceSelector
          currentRoundId={roundId}
          selectedRounds={sourceRoundIds}
          setSelectedRounds={setSourceRoundIds}
          allowedRoundIds={allowedRoundIds}
          hideAccordion={!!setSourceRoundIds}
        />
      )}
    </Box>
  );
}
